/**
 * /app — landing surface. Per `docs/frontend/035-frontend-app-shell-plan.md §3.1`,
 * this route bootstraps `me-context` (already done by the provider before
 * the AuthGate admits us) and routes the user to a sensible default.
 *
 * Behaviour (gate order): sysadmin → designer (Task 208) → video producer
 * (Task 217) → senior_content_writer → content_writer (Task 219) → agency
 * production-execute redirect → single-client bounce → owner / marketing_manager
 * / account_manager / media_buyer command centers → Task 200 role-aware home.
 *   - sysadmin → render the support-mode banner card; no tenant data.
 *   - `designer` → DesignerWorkspace role home (Task 208); `video_editor` /
 *     `videographer` → VideoProducerWorkspace role home (Task 217);
 *     `senior_content_writer` → SeniorContentWriterWorkspace (review) and
 *     `content_writer` → ContentWriterWorkspace (authoring) — Task 219. All run
 *     BEFORE the production redirect and carve those roles out of it.
 *   - the REMAINING agency production-execute staff (senior_designer / … ) →
 *     redirect to /app/me/tasks (their focused work queue; Task 118).
 *   - non-leadership with exactly one assigned client → bounce to that
 *     client's overview to skip an unnecessary click.
 *   - Task 200 — role-aware home: account_manager + the remaining specialist
 *     roles (media_buyer / content) lead with their ASSIGNED clients (or a
 *     clear "no clients assigned yet" empty state), not the agency-wide browse.
 *     Leadership keeps the agency-wide entry grid.
 *   - empty workspace + actor has `client.create` → onboarding block
 *     linking to `/app/clients?new=1` (Task 043; leadership only).
 *
 * UI-only: every card/link below is still gated by the same permissions the
 * sidebar uses; RLS remains the load-bearing security gate.
 */

import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import type { ClientStatus } from '../api/types';
import { useClientScope } from '../context/ClientScopeProvider';
import { useMeContext } from '../context/MeContextProvider';
import {
  hasAnyPermission,
  hasRoleCategory,
  isAccountManager,
  isAgencyProductionOnly,
  isAssignedClientScopedHome,
  isContentWriter,
  isDesigner,
  isLeadership,
  isMarketingManager,
  isMediaBuyer,
  isOwner,
  isSeniorContentWriter,
  isVideoProducer,
  PERM,
  primaryAgencyRole,
  roleLabelAr,
} from '../permissions';
import { AccountManagerCommandCenter } from './AccountManagerCommandCenter';
import { ContentWriterWorkspace } from './ContentWriterWorkspace';
import { DesignerWorkspace } from './DesignerWorkspace';
import { MarketingManagerCommandCenter } from './MarketingManagerCommandCenter';
import { MediaBuyerCommandCenter } from './MediaBuyerCommandCenter';
import { OwnerCommandCenter } from './OwnerCommandCenter';
import { SeniorContentWriterWorkspace } from './SeniorContentWriterWorkspace';
import { VideoProducerWorkspace } from './VideoProducerWorkspace';

const INFLUENCER_SURFACES_ENABLED = false;

function clientStatusLabelAr(status: ClientStatus): string {
  switch (status) {
    case 'active':
      return 'نشط';
    case 'onboarding':
      return 'تأهيل';
    case 'paused':
      return 'متوقف';
    case 'archived':
      return 'مؤرشف';
    default:
      return status;
  }
}

export function AppLanding() {
  const { me } = useMeContext();
  const clientScope = useClientScope();
  const location = useLocation();
  if (!me) return null;

  // Task 208 — Designer Workspace is the `designer` ROLE HOME. `designer` is a
  // PRODUCTION-execute role (holds production.asset.submit), so this gate runs
  // BEFORE the production redirect below and intentionally CARVES designers out
  // of it: ANY designer (zero-, single-, or multi-client) lands on the 3-pane
  // creative workspace, and a designer with no assigned clients sees the
  // workspace's own empty state (NOT /app/me/tasks). It is guarded against the
  // higher command-center roles (owner / marketing_manager / account_manager /
  // media_buyer) so those keep precedence. `video_editor` / `videographer` get
  // their own Video Producer Workspace at the NEXT gate (Task 217); the
  // remaining production-execute roles (content_writer / senior_* …) still hit
  // the production redirect.
  // Assigned clients ONLY (me.client_assignments); read-only, no owner controls.
  if (
    !me.system_admin &&
    !isOwner(me) &&
    !isMarketingManager(me) &&
    !isAccountManager(me) &&
    !isMediaBuyer(me) &&
    isDesigner(me)
  ) {
    return <DesignerWorkspace me={me} />;
  }

  // Task 217 — Video Producer Workspace is the role HOME for the agency
  // video-production roles `video_editor` / `videographer` (there is no
  // `video_producer` role). Like the designer gate above, it runs BEFORE the
  // production redirect and CARVES video producers out of it: ANY video producer
  // (zero-, single-, or multi-client) lands on the workspace; a zero-client one
  // sees its empty state (NOT /app/me/tasks). Guarded against the higher
  // command-center roles AND `designer` (a designer+video_editor dual-hat
  // resolves to the Designer Workspace above — designer precedence). Every OTHER
  // production-only role (content_writer, etc.) still hits the production
  // redirect. Assigned clients ONLY; read-only, no owner controls.
  if (
    !me.system_admin &&
    !isOwner(me) &&
    !isMarketingManager(me) &&
    !isAccountManager(me) &&
    !isMediaBuyer(me) &&
    !isDesigner(me) &&
    isVideoProducer(me)
  ) {
    return <VideoProducerWorkspace me={me} />;
  }

  // Task 219 — Senior Content Writer Workspace (review home). senior_content_writer
  // is a PRODUCTION-execute role; this gate runs BEFORE the production redirect
  // and carves it out. It runs BEFORE the content_writer gate below, so a
  // content_writer + senior_content_writer dual-hat resolves to the SENIOR review
  // surface. Guarded against the higher command-center roles + designer + video.
  if (
    !me.system_admin &&
    !isOwner(me) &&
    !isMarketingManager(me) &&
    !isAccountManager(me) &&
    !isMediaBuyer(me) &&
    !isDesigner(me) &&
    !isVideoProducer(me) &&
    isSeniorContentWriter(me)
  ) {
    return <SeniorContentWriterWorkspace me={me} />;
  }

  // Task 219 — Content Writer Workspace (authoring home). content_writer's
  // deliverable is in-system TEXT (Task 220 draft RPCs/EFs), not a file upload.
  // Carved out of the production redirect; guarded against all higher surfaces
  // (incl. senior_content_writer, which resolves to its own review workspace
  // above). Every OTHER production-only role still hits the production redirect.
  if (
    !me.system_admin &&
    !isOwner(me) &&
    !isMarketingManager(me) &&
    !isAccountManager(me) &&
    !isMediaBuyer(me) &&
    !isDesigner(me) &&
    !isVideoProducer(me) &&
    !isSeniorContentWriter(me) &&
    isContentWriter(me)
  ) {
    return <ContentWriterWorkspace me={me} />;
  }

  // Task 118 — Employee Workspace v1. Agency production-execute staff
  // (designer / video_editor / content_writer / etc., but NOT leadership
  // and NOT sysadmin) get a focused "مهامي" landing surface. This redirect
  // runs BEFORE the single-client bounce below so a designer with one
  // client lands on their work queue, not the generic client overview.
  if (isAgencyProductionOnly(me)) {
    return location.search ? (
      <Navigate to={`/app/me/tasks${location.search}`} replace />
    ) : (
      <Navigate to="/app/me/tasks" replace />
    );
  }

  if (!me.system_admin && !isLeadership(me) && me.client_assignments.length === 1) {
    const a = me.client_assignments[0];
    if (a) return <Navigate to={`/app/clients/${a.client_id}/overview`} replace />;
  }

  // Task 204 — Owner / Agency-Founder Command Center. OWNER-ONLY for now
  // (NOT sysadmin). `marketing_manager` is leadership too, but it keeps the
  // existing agency-wide home until its dedicated Marketing Manager Command
  // Center ships in a follow-up PR — so this gate uses isOwner, not
  // isLeadership. Runs AFTER the production-staff redirect and the
  // non-leadership single-client bounce above (both still keyed on
  // isLeadership, so marketing_manager is unaffected by them). Every
  // non-owner role keeps its existing Task 200 home below.
  if (!me.system_admin && isOwner(me)) {
    return <OwnerCommandCenter me={me} />;
  }

  // Task 205 — Marketing Manager Command Center. marketing_manager-only (NOT
  // sysadmin). Runs AFTER the owner gate, so an actor holding BOTH owner and
  // marketing_manager resolves to the Owner Command Center (owner precedence).
  // A pure marketing_manager lands here instead of the generic agency-wide
  // home. Production redirect + single-client bounce above are keyed on
  // isAgencyProductionOnly / !isLeadership, so marketing_manager is unaffected
  // by them. No owner-management controls are exposed here.
  if (!me.system_admin && isMarketingManager(me)) {
    return <MarketingManagerCommandCenter me={me} />;
  }

  // Task 206 — Account Manager Command Center. account_manager-only (NOT
  // sysadmin), and only for MULTIPLE assigned clients. Runs AFTER the owner +
  // marketing_manager gates (so a dual-role actor resolves to the higher
  // surface). A single-client account_manager was already bounced to that
  // client above (line ~72, still keyed on !isLeadership), and a zero-client
  // account_manager falls through to the Task 200 scoped empty state below —
  // both intentionally preserved. Specialists (media_buyer / design / video)
  // are NOT account_manager, so they keep their Task 200 assigned-clients
  // home. Shows assigned clients ONLY (from me.client_assignments).
  if (!me.system_admin && isAccountManager(me) && me.client_assignments.length > 1) {
    return <AccountManagerCommandCenter me={me} />;
  }

  // Task 207 — Media Buyer Command Center. media_buyer-only (NOT sysadmin),
  // and only for MULTIPLE assigned clients. `media_buyer` is a specialist, so
  // this gate carves a multi-client media_buyer out of the Task 200 specialist
  // scoped home below. Runs AFTER owner / marketing_manager / account_manager
  // gates (a dual-role actor resolves to the higher surface). A single-client
  // media_buyer was already bounced above (line ~74), and a zero-client one
  // falls through to the Task 200 scoped empty state — both preserved. Other
  // specialists (design / video) are NOT media_buyer, so their scoped home is
  // untouched. Assigned clients ONLY; read-only paid-media monitoring.
  if (!me.system_admin && isMediaBuyer(me) && me.client_assignments.length > 1) {
    return <MediaBuyerCommandCenter me={me} />;
  }

  const perms = me.permissions;
  const roleKey = primaryAgencyRole(me);
  const roleLabel = roleKey ? roleLabelAr(roleKey) : null;
  // Task 200 — account_manager + specialists lead with assigned clients.
  const scoped = isAssignedClientScopedHome(me);
  const assignments = me.client_assignments;
  const canCreateClient = !me.system_admin && hasAnyPermission(perms, PERM.CLIENT_CREATE);
  const workspaceIsEmpty = clientScope.status === 'ready' && clientScope.clients.length === 0;
  const showInfluencers =
    INFLUENCER_SURFACES_ENABLED &&
    !me.system_admin &&
    hasAnyPermission(perms, PERM.INFLUENCER_CATALOG_READ);
  const showCrm =
    !me.system_admin && hasAnyPermission(perms, PERM.CRM_LEAD_READ, PERM.CRM_DEAL_READ);
  const showReports =
    !me.system_admin && hasAnyPermission(perms, PERM.REPORT_READ, PERM.REPORT_FINANCE_READ);
  const showCreative = !me.system_admin && hasAnyPermission(perms, PERM.CREATIVE_INTELLIGENCE_READ);
  const showPaidMedia =
    !me.system_admin &&
    hasAnyPermission(perms, PERM.PAID_MEDIA_ACCOUNT_READ, PERM.PAID_MEDIA_CAMPAIGN_READ);
  const showServices =
    !me.system_admin &&
    hasRoleCategory(me, 'agency') &&
    hasAnyPermission(perms, PERM.BILLING_READ, PERM.CONTRACT_READ);
  const showNotifications = !me.system_admin && hasAnyPermission(perms, PERM.NOTIFICATION_READ);

  const intro = me.system_admin
    ? 'أنت مسجّل كـ System admin. هذا الحساب يخدم أغراض الدعم فقط ولا يملك سياق وكالة.'
    : scoped
      ? 'هذه مساحتك. تظهر هنا العملاء المعيّنون لك وعملك المرتبط بهم فقط — لا تظهر بقية عملاء الوكالة.'
      : me.organization
        ? `أنت داخل مساحة ${me.organization.name}. اختر عميلاً من الشريط الجانبي أو افتح أحد المسارات على مستوى الوكالة بالأسفل.`
        : 'سيتم تحميل سياق الوكالة فور توفّر التعيينات.';

  return (
    <div className="stack-md">
      <div>
        <h1 className="h-page">
          {me.system_admin || !roleLabel ? (
            <>
              مرحباً{' '}
              <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
                في Super agency
              </em>
            </>
          ) : (
            <>
              مرحباً،{' '}
              <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
                {roleLabel}
              </em>
            </>
          )}
        </h1>
        <p className="body" style={{ marginTop: 8, maxWidth: 680 }}>
          {intro}
        </p>
      </div>

      {/*
        Task 200 — assigned-clients-first for account_manager + specialists.
        With exactly one assignment the actor was already bounced above, so
        this renders either the multi-client grid or the no-clients empty
        state. Leadership skips this block and uses the agency-wide grid.
      */}
      {!me.system_admin && scoped ? (
        assignments.length > 0 ? (
          <section className="stack-sm">
            <span className="lbl">عملاؤك المعيّنون ({assignments.length})</span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))',
                gap: 14,
              }}
            >
              {assignments.map((a) => (
                <LandingCard
                  key={a.client_id}
                  to={`/app/clients/${a.client_id}/overview`}
                  eyebrow={clientStatusLabelAr(a.client_status)}
                  title={a.client_name}
                  body="افتح مساحة العميل لمتابعة المحتوى والإنتاج والحملات."
                />
              ))}
            </div>
          </section>
        ) : (
          <div
            className="card"
            style={{ padding: 22, background: 'var(--cream-2)', border: '1px solid var(--line)' }}
          >
            <div className="lbl">لا يوجد عملاء بعد</div>
            <h2 className="h-section" style={{ marginTop: 6, marginBottom: 8 }}>
              لم تُسنَد إليك أي عملاء حتى الآن
            </h2>
            <p className="body" style={{ maxWidth: 560 }}>
              يتولّى مالك الوكالة أو مدير التسويق تعيين العملاء على فريق العمل. فور تعيينك على عميل،
              ستظهر مساحته هنا مباشرةً ويمكنك بدء العمل عليه. تابع إشعاراتك من الأعلى ريثما يتم
              التعيين.
            </p>
          </div>
        )
      ) : null}

      {!me.system_admin && workspaceIsEmpty && canCreateClient ? (
        <div
          className="card"
          style={{
            padding: 22,
            background: 'linear-gradient(135deg, var(--cream-2) 0%, var(--tangerine-soft) 100%)',
            border: '1px solid var(--tangerine)',
          }}
        >
          <div className="lbl">أول خطوة</div>
          <h2 className="h-section" style={{ marginTop: 6, marginBottom: 8 }}>
            ابدأ بإضافة أول عميل
          </h2>
          <p className="body" style={{ marginBottom: 14, maxWidth: 560 }}>
            مساحة عملك فارغة حالياً. أضف أول عميل لتفعيل الـ workspace (نظرة عامة، استراتيجية،
            محتوى، إعلانات، تقارير).
          </p>
          <Link
            to="/app/clients?new=1"
            className="auth-cta"
            style={{ width: 'auto', textDecoration: 'none' }}
          >
            <span>إضافة أول عميل</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </div>
      ) : null}

      {!me.system_admin ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))',
            gap: 14,
          }}
        >
          {/*
            Leadership + agency_other keep the agency-wide Clients entry.
            Scoped roles (account_manager / specialists) get the dedicated
            "assigned clients" section above instead, so the agency-wide
            browse is de-emphasised for them.
          */}
          {!scoped ? (
            <LandingCard
              to="/app/clients"
              eyebrow="الوكالة"
              title={`العملاء (${me.client_assignments.length || '—'})`}
              body="افتح workspace أي عميل لمتابعة المحتوى والإعلانات والتقارير."
            />
          ) : null}
          {showInfluencers ? (
            <LandingCard
              to="/app/influencers"
              eyebrow="كتالوج"
              title="المؤثرون"
              body="استعرض قاعدة المؤثرين على مستوى الوكالة وحساباتهم على المنصات."
            />
          ) : null}
          {showCrm ? (
            <LandingCard
              to="/app/crm"
              eyebrow="مبيعات"
              title="CRM"
              body="العملاء المحتملون والصفقات في قمع المبيعات (قراءة فقط في MVP)."
            />
          ) : null}
          {showReports ? (
            <LandingCard
              to="/app/reports"
              eyebrow="تقارير"
              title="التقارير"
              body="كل طلبات التقارير المرئية لك، مع نوع التقرير والعميل والحالة."
            />
          ) : null}
          {showCreative ? (
            <LandingCard
              to="/app/creative"
              eyebrow="إبداع"
              title="مكتبة الإبداع"
              body="الأصول الإبداعية المتاحة لك، مع روابط الحملات والمحتوى."
            />
          ) : null}
          {showPaidMedia ? (
            <LandingCard
              to="/app/paid-media"
              eyebrow="إعلانات مدفوعة"
              title="الإعلانات المدفوعة"
              body="الحملات والحسابات الإعلانية للعملاء المتاحين لك."
            />
          ) : null}
          {showServices ? (
            <LandingCard
              to="/app/services"
              eyebrow="تجاري"
              title="الخدمات والباقات"
              body="كتالوج الخدمات والباقات التي يقدّمها المكتب، وتعيينها للعملاء."
            />
          ) : null}
          {showNotifications ? (
            <LandingCard
              to="/app/notifications"
              eyebrow="صندوق الإشعارات"
              title="الإشعارات"
              body="آخر الأحداث المرتبطة بعملك (read-only في MVP)."
            />
          ) : null}
          <LandingCard
            to="/app/settings"
            eyebrow="الحساب"
            title="الإعدادات والصلاحيات"
            body="عرض ملفك الشخصي، الصلاحيات الفعّالة، اللغة والمنطقة الزمنية."
          />
        </div>
      ) : null}
    </div>
  );
}

function LandingCard({
  to,
  eyebrow,
  title,
  body,
}: {
  readonly to: string;
  readonly eyebrow: string;
  readonly title: string;
  readonly body: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="card"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <span className="lbl">{eyebrow}</span>
      <span className="h-section">{title}</span>
      <span className="body">{body}</span>
    </Link>
  );
}
